import { Group, Stack, Text, TextInput } from '@mantine/core';
import {
  If,
  TooltipInfo,
  VLazyLoadMultiSelect,
  VLazyLoadSelect,
  VMultiSelect,
  VSelect,
  VSelectOptionWithDescription,
  VSwitch,
} from '@vision/ui/components';
import { ChannelType, SchemaType } from '@vision/ui/enums';
import { useChannelTypes, useEmojiContext, useSelectedAccountId } from '@vision/ui/hooks';
import {
  ReportChartGroupCompareItemType,
  ReportsDetailGraphicSchemaValues,
  SimpleChannel,
  SimpleFlowQuestion,
  SimpleFlowQuestionOption,
} from '@vision/ui/interfaces';
import { QUESTION_STYLES_WITH_OPTIONS } from '@vision/ui/pages/ReportsDetailGraphic/constants';
import {
  useGetSchemaFieldsV2Query,
  useLazyFetchChannelsWithPaginationQuery,
  useLazyFetchFlowQuestionOptionsWithPaginationQuery,
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
  useLazyGetSchemaFieldsV2Query,
} from '@vision/ui/services';
import { getBodyText, insertIfObject, translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { ChangeEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReportsDetailGraphicCompareLayout,
  ReportsDetailGraphicCompareSchemaValues,
} from '../../../ReportsDetailGraphicCompareComponents';
import { ReportsDetailGraphicConfigurationValueHeader } from '../../../ReportsDetailGraphicConfigurationValueHeader';

const FCR_CAMPAIGN_TYPES = [
  ChannelType.LINK,
  ChannelType.EMAIL,
  ChannelType.SMS,
  ChannelType.PUSH_NOTIFICATION,
  ChannelType.WHATSAPP,
  ChannelType.WEB_WIDGET,
];

const PIE_CHART_FCR_COMPARE_ITEMS: ReportChartGroupCompareItemType[] = ['question-option'];

const DISTRIBUTION_ADJUSTMENT_OPERATORS = [
  { value: 'exact', labelKey: 'operators.equals' },
  { value: 'not_contains', labelKey: 'operators.not_equals' },
];

interface ReportChartFcrValueComponentProps {
  supportsCustomerSchema?: boolean;
}

function ReportChartFcrValueComponent({ supportsCustomerSchema = false }: ReportChartFcrValueComponentProps) {
  const { t, i18n } = useTranslation(['translation', 'page-reports']);
  const { textWithNativeEmoji } = useEmojiContext();
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const campaignTypeSelectItems = useChannelTypes(FCR_CAMPAIGN_TYPES);
  const isFcrMeasure = formik.values.selectedMeasure === 'feedback-fcr';
  const isFlowSelected = formik.values.selectedCompareCriteria.selectedQuestionOptionFlowIds.length > 0;
  const isQuestionSelected = formik.values.selectedCompareCriteria.selectedQuestionOptionQuestionIds.length > 0;
  const isCustomerSchemaSelected = formik.values.selectedCompareCriteria.selectedSchemaCodes.length > 0;
  const compareItems = useMemo<ReportChartGroupCompareItemType[]>(
    () => [...PIE_CHART_FCR_COMPARE_ITEMS, ...(supportsCustomerSchema ? (['customer-schema'] as const) : [])],
    [supportsCustomerSchema],
  );
  const isDistributionAdjustmentActive = !!formik.values.selectedCustomize?.distributionAdjustment?.active;
  const { data: distributionSchemaFieldsResponse } = useGetSchemaFieldsV2Query(
    { accountId, schemaType: SchemaType.DISPATCH },
    { skip: !accountId || !isDistributionAdjustmentActive },
  );
  const distributionSchemaFields = distributionSchemaFieldsResponse?.data ?? [];

  useEffect(() => {
    if (!isFcrMeasure) {
      return;
    }

    const isCompareLocked =
      formik.values.selectedCompare.length === compareItems.length &&
      compareItems.every((compareItem) => formik.values.selectedCompare.includes(compareItem));

    if (isCompareLocked) {
      return;
    }

    formik.setFieldValue('selectedCompare', compareItems);
  }, [compareItems, formik.values.selectedCompare, isFcrMeasure]);

  if (!isFcrMeasure) {
    return null;
  }

  const handleCampaignTypeChange = (value: string) => {
    formik.setValues({
      ...formik.values,
      selectedCustomize: {
        ...formik.values.selectedCustomize,
        campaignType: value as ChannelType,
      },
      selectedFilters: {
        ...formik.values.selectedFilters,
        selectedChannelIds: [],
      },
    });
  };

  const handleFlowChange = (value: string) => {
    formik.setValues({
      ...formik.values,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        selectedQuestionOptionFlowIds: value ? [value] : [],
        selectedQuestionOptionQuestionIds: [],
        selectedQuestionOptionIds: [],
      },
    });
  };

  const handleQuestionChange = (value: string) => {
    formik.setValues({
      ...formik.values,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        selectedQuestionOptionQuestionIds: value ? [value] : [],
        selectedQuestionOptionIds: [],
      },
    });
  };

  const handleCustomerSchemaChange = (value: string) => {
    formik.setValues({
      ...formik.values,
      selectedCompare: compareItems,
      selectedCompareCriteria: {
        ...formik.values.selectedCompareCriteria,
        selectedSchemaCodes: value ? [value] : [],
        selectedSchemaTagIds: [],
      },
    });
  };

  const handleDistributionAdjustmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const active = event.currentTarget.checked;

    formik.setValues({
      ...formik.values,
      selectedCustomize: {
        ...formik.values.selectedCustomize,
        distributionAdjustment: {
          active,
          filter: active ? (formik.values.selectedCustomize?.distributionAdjustment?.filter ?? null) : null,
          operator: active ? (formik.values.selectedCustomize?.distributionAdjustment?.operator ?? null) : null,
          value: active ? (formik.values.selectedCustomize?.distributionAdjustment?.value ?? '') : '',
        },
      },
    });
  };

  const handleDefaultCampaignValuesLoaded = (items: SimpleChannel[]) => {
    const newIds = formik.values.selectedFilters.selectedChannelIds.filter((id) =>
      items.some((item) => item.id === id),
    );
    formik.setFieldValue('selectedFilters.selectedChannelIds', newIds.slice(0, 1));
  };

  const handleDefaultQuestionValuesLoaded = (data: SimpleFlowQuestion[]) => {
    const newIds = formik.values.selectedCompareCriteria.selectedQuestionOptionQuestionIds.filter((id) =>
      data.some((item) => item.id === id),
    );

    formik.setFieldValue('selectedCompareCriteria.selectedQuestionOptionQuestionIds', newIds.slice(0, 1));
  };

  const handleDefaultQuestionOptionValuesLoaded = (data: SimpleFlowQuestionOption[]) => {
    const newIds = formik.values.selectedCompareCriteria.selectedQuestionOptionIds.filter((id) =>
      data.some((item) => item.id === id),
    );

    formik.setFieldValue('selectedCompareCriteria.selectedQuestionOptionIds', newIds);
  };

  const mapQuestionOptionToSelectOption = (item: SimpleFlowQuestionOption) => ({
    value: item.id,
    label: textWithNativeEmoji(getBodyText(item.body, i18n.language)),
    ...insertIfObject(!!item?.question_body, {
      description: textWithNativeEmoji(getBodyText(item.question_body, i18n.language)),
    }),
  });

  const compareSelectItems = compareItems.map((item) => ({
    value: item,
    label: t(`page-reports:reportCompareGroupItemType.${item}`),
  }));

  return (
    <Stack gap={10}>
      <VSelect
        id="report-fcr-campaign-type"
        data-testid="input-report-fcr-campaign-type"
        label={t('page-reports:campaignType')}
        data={campaignTypeSelectItems}
        value={formik.values.selectedCustomize?.campaignType}
        onChange={handleCampaignTypeChange}
        error={translateErrorMessage(formik, 'selectedCustomize.campaignType')}
        withAsterisk={true}
        clearable={true}
      />

      <VLazyLoadSelect
        id="report-fcr-campaign"
        data-testid="input-report-fcr-campaign"
        label={t('page-reports:campaign')}
        useLazyApiQueryFunction={useLazyFetchChannelsWithPaginationQuery}
        apiRequestParameters={{
          nodeId: accountId,
          types: [formik.values.selectedCustomize?.campaignType as ChannelType],
          sortBy: 'name',
          sortOrder: 'asc',
        }}
        multiSelectItemMapper={(items) =>
          items.map((item) => ({
            value: item.id,
            label: item.name,
            description: item?.type,
          }))
        }
        searchKey="node_name"
        value={formik.values.selectedFilters.selectedChannelIds[0] ?? null}
        onChange={(value) => formik.setFieldValue('selectedFilters.selectedChannelIds', value ? [value] : [])}
        onDefaultValuesLoaded={handleDefaultCampaignValuesLoaded}
        error={translateErrorMessage(formik, 'selectedFilters.selectedChannelIds')}
        disabled={!formik.values.selectedCustomize?.campaignType}
        withAsterisk={true}
        resetChangeRequestParameters={true}
        itemComponent={VSelectOptionWithDescription}
      />

      <VLazyLoadSelect
        id="report-fcr-flow"
        data-testid="input-report-fcr-flow"
        label={t('flows')}
        useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
        apiRequestParameters={{ nodeId: accountId, sortBy: 'name', sortOrder: 'asc' }}
        multiSelectItemMapper={(items) => items.map((item) => ({ value: item.id, label: item.name }))}
        searchKey="flow_name"
        value={formik.values.selectedCompareCriteria.selectedQuestionOptionFlowIds[0] ?? null}
        onChange={handleFlowChange}
        error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionOptionFlowIds')}
        withAsterisk={true}
      />

      <If value={isFlowSelected}>
        <VLazyLoadSelect
          id="report-fcr-question-title"
          data-testid="input-report-fcr-question-title"
          label={t('page-reports:questionTitle')}
          useLazyApiQueryFunction={useLazyFetchFlowQuestionsWithPaginationQuery}
          apiRequestParameters={{
            nodeId: accountId,
            flowIds: formik.values.selectedCompareCriteria.selectedQuestionOptionFlowIds,
            style: QUESTION_STYLES_WITH_OPTIONS,
            sortBy: 'name',
            sortOrder: 'asc',
          }}
          multiSelectItemMapper={(items) =>
            items
              .filter((item) => item.body !== null)
              .map((item) => ({
                value: item.id,
                label: textWithNativeEmoji(getBodyText(item.body, i18n.language)),
                description: item?.flow_name,
              }))
          }
          searchKey="question_body"
          value={formik.values.selectedCompareCriteria.selectedQuestionOptionQuestionIds[0] ?? null}
          onChange={handleQuestionChange}
          onDefaultValuesLoaded={handleDefaultQuestionValuesLoaded}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionOptionQuestionIds')}
          withAsterisk={true}
          resetChangeRequestParameters={true}
          itemComponent={VSelectOptionWithDescription}
        />
      </If>

      <If value={isQuestionSelected}>
        <VLazyLoadMultiSelect
          id="report-fcr-question-option"
          data-testid="input-report-fcr-question-option"
          label={t('questionOption')}
          useLazyApiQueryFunction={useLazyFetchFlowQuestionOptionsWithPaginationQuery}
          apiRequestParameters={{
            nodeId: accountId,
            questionIds: formik.values.selectedCompareCriteria.selectedQuestionOptionQuestionIds,
            sortBy: 'name',
            sortOrder: 'asc',
          }}
          multiSelectItemMapper={(items) => items.map(mapQuestionOptionToSelectOption)}
          searchKey="option_body"
          value={formik.values.selectedCompareCriteria.selectedQuestionOptionIds}
          onChange={(value) => formik.setFieldValue('selectedCompareCriteria.selectedQuestionOptionIds', value)}
          onDefaultValuesLoaded={handleDefaultQuestionOptionValuesLoaded}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionOptionIds')}
          withAsterisk={true}
          resetChangeRequestParameters={true}
          withSelectAll={true}
          itemComponent={VSelectOptionWithDescription}
        />
      </If>

      <Group justify="space-between" align="center" my={10}>
        <Group gap={5}>
          <Text fw={500}>{t('page-reports:applyDistributionAdjustment')}</Text>
          <TooltipInfo>{t('page-reports:applyDistributionAdjustmentTooltip')}</TooltipInfo>
        </Group>

        <VSwitch
          data-testid="input-report-fcr-apply-distribution-adjustment"
          checked={isDistributionAdjustmentActive}
          onChange={handleDistributionAdjustmentChange}
        />
      </Group>

      <If value={isDistributionAdjustmentActive}>
        <VSelect
          id="report-fcr-distribution-filter"
          data-testid="input-report-fcr-distribution-filter"
          label={t('page-reports:distributionFilter')}
          data={distributionSchemaFields.map((item) => ({
            value: item.field_code,
            label: item.field_name,
          }))}
          value={formik.values.selectedCustomize?.distributionAdjustment?.filter ?? null}
          onChange={(value) => formik.setFieldValue('selectedCustomize.distributionAdjustment.filter', value)}
          error={translateErrorMessage(formik, 'selectedCustomize.distributionAdjustment.filter')}
          withAsterisk={true}
          searchable={true}
          clearable={true}
        />

        <VSelect
          id="report-fcr-distribution-operator"
          data-testid="input-report-fcr-distribution-operator"
          label={t('page-reports:operator')}
          data={DISTRIBUTION_ADJUSTMENT_OPERATORS.map((item) => ({
            value: item.value,
            label: t(item.labelKey),
          }))}
          value={formik.values.selectedCustomize?.distributionAdjustment?.operator ?? null}
          onChange={(value) => formik.setFieldValue('selectedCustomize.distributionAdjustment.operator', value)}
          error={translateErrorMessage(formik, 'selectedCustomize.distributionAdjustment.operator')}
          withAsterisk={true}
          clearable={true}
        />

        <TextInput
          id="report-fcr-distribution-value"
          data-testid="input-report-fcr-distribution-value"
          label={t('page-reports:value')}
          value={formik.values.selectedCustomize?.distributionAdjustment?.value ?? ''}
          onChange={(event) =>
            formik.setFieldValue('selectedCustomize.distributionAdjustment.value', event.currentTarget.value)
          }
          error={translateErrorMessage(formik, 'selectedCustomize.distributionAdjustment.value')}
          withAsterisk={true}
        />
      </If>

      <ReportsDetailGraphicConfigurationValueHeader label={t('page-reports:compare')} />

      {compareItems.length > 1 ? (
        <VMultiSelect
          id="report-fcr-compare"
          data-testid="input-report-fcr-compare"
          label={t('page-reports:compare')}
          data={compareSelectItems}
          value={compareItems}
          disabled={true}
          withAsterisk={true}
          showAllSelectedPill={false}
          withSelectAll={false}
        />
      ) : (
        <VSelect
          id="report-fcr-compare"
          data-testid="input-report-fcr-compare"
          label={t('page-reports:compare')}
          data={compareSelectItems}
          value={compareItems[0]}
          disabled={true}
          withAsterisk={true}
        />
      )}

      <If value={supportsCustomerSchema}>
        <ReportsDetailGraphicCompareLayout title={t('schema')}>
          <Stack gap={12}>
            <VLazyLoadSelect
              id="report-fcr-customer-schema"
              data-testid="input-report-fcr-customer-schema"
              label={t('schema')}
              useLazyApiQueryFunction={useLazyGetSchemaFieldsV2Query}
              apiRequestParameters={{ accountId, type: SchemaType.CUSTOMER }}
              multiSelectItemMapper={(items) =>
                items.map((item) => ({
                  value: item.id,
                  label: item.field_name,
                }))
              }
              searchKey="search_string"
              value={formik.values.selectedCompareCriteria.selectedSchemaCodes[0] ?? null}
              onChange={handleCustomerSchemaChange}
              error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedSchemaCodes')}
              resetChangeRequestParameters={true}
              withAsterisk={true}
            />

            <If value={isCustomerSchemaSelected}>
              <ReportsDetailGraphicCompareSchemaValues
                key={formik.values.selectedCompareCriteria.selectedSchemaCodes.join(',')}
                accountId={accountId}
                schemaFieldIds={formik.values.selectedCompareCriteria.selectedSchemaCodes}
                value={formik.values.selectedCompareCriteria.selectedSchemaTagIds || []}
                onChange={(value) => formik.setFieldValue('selectedCompareCriteria.selectedSchemaTagIds', value)}
                onBlur={formik.handleBlur}
                error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedSchemaTagIds')}
              />
            </If>
          </Stack>
        </ReportsDetailGraphicCompareLayout>
      </If>
    </Stack>
  );
}

export function PieChartFcrValueComponent() {
  return <ReportChartFcrValueComponent />;
}

export function TableChartFcrValueComponent() {
  return <ReportChartFcrValueComponent supportsCustomerSchema={true} />;
}
